import styles from './styles.scss';
import { IconMenu } from 'components/Shared/Icon';
import { match, NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { MenuDrawer } from 'components/Shared/Drawer';
import { Button } from 'components/Button';
import connect from 'components/Shared/connect';
import { isMobile, isTablet } from 'components/Shared/responsive';
import { openMainMenu, closeMainMenu } from 'components/MainMenu/actions';
import { MenuTree } from './menuTree';
import { IMenuItem } from 'models/IMenuItem';
import { Navigation } from 'components/Navigation';
import { NavigationBlock } from 'models/NavigationBlock';
import { ContactNavigationBlock, isContactNavigationBlock } from 'models/ContactNavigationBlock';
import { isStoresNavigationBlock, StoresNavigationBlock } from 'models/StoresNavigationBlock';
import { BrandsNavigationBlock, isBrandsNavigationBlock } from 'models/BrandsNavigationBlock';
import { SiteModeNavigationBlock, isSiteModeNavigationBlock } from 'models/SiteModeNavigationBlock';
import {
  CustomerPageNavigationBlock,
  isCustomerPageNavigationBlock
} from 'models/CustomerPageNavigationBlock';
import { ContactBlock } from './contact/contactBlock';
import { StoresBlock } from './stores/storesBlock';
import { BrandsBlock } from './brands/brandsBlock';
import { SiteModeBlock } from './siteMode/siteModeBlock';
import { NavigationRoot } from 'models/NavigationRoot';
import { ReactElement } from 'react';
import { selectBlackFridayStatus } from 'components/Header/themes/BGH/selector';
import { useSelector } from 'react-redux';
import cn from 'classnames';

const navigationMap = match<NavigationBlock | NavigationRoot, ReactElement | null>()
  .when<ContactNavigationBlock>(isContactNavigationBlock, item => (
    <ContactBlock key={`cb-${item.header}`} {...item} />
  ))
  .when<StoresNavigationBlock>(isStoresNavigationBlock, item => (
    <StoresBlock key={`sb-${item.header}`} {...item} />
  ))
  .when<BrandsNavigationBlock>(isBrandsNavigationBlock, item => (
    <BrandsBlock key={`bb-${item.header}`} {...item} />
  ))
  .when<CustomerPageNavigationBlock>(isCustomerPageNavigationBlock, item => (
    <BrandsBlock key={`cpb-${item.header}`} {...item} />
  ))
  .when<SiteModeNavigationBlock>(isSiteModeNavigationBlock, item => (
    <SiteModeBlock key={`sm-${item.header}`} {...item} />
  ))
  .or(NO_ELEMENT)
  .toFunc();

const BLOCK_WIDTH = 100;
const ANIMATION_DELAY = 250;
const Root = connect<MergedProps>(state => ({
  items: state.header.mainMenu.items,
  subNavigation: state.header.subNavigation
}))(({ items, subNavigation }: MergedProps) => {
  return (
    <div>
      <MenuTree items={items} />
      {subNavigation && subNavigation.map(navigationMap)}
    </div>
  );
});

interface MergedProps {
  isOpen: boolean;
  isMobile: boolean;
  isTablet: boolean;
  items: IMenuItem[];
  closeMainMenu: () => void;
  openMainMenu: () => void;
  subNavigation: NavigationBlock[];
}

// iPhone does not cope well with flex-grow.
const getMenuHeight = ({ isMobile, isTablet }: { isMobile: boolean; isTablet: boolean }) => {
  const MOBILE_HEIGHT = 64;
  const TABLET_HEIGHT = 76;

  const getStyleHeight = ({ height }: { height: number }) => `Calc(100% - ${height}px)`;

  if (isMobile) {
    return getStyleHeight({ height: MOBILE_HEIGHT });
  }

  if (isTablet) {
    return getStyleHeight({ height: TABLET_HEIGHT });
  }

  return getStyleHeight({ height: 0 });
};

export default connect<MergedProps>(
  state => ({
    isMobile: isMobile(state),
    isTablet: isTablet(state),
    isOpen: state.header.mainMenu.isOpen
  }),
  dispatch => ({
    closeMainMenu: () => dispatch(closeMainMenu()),
    openMainMenu: () => dispatch(openMainMenu())
  })
)(({ isMobile, isTablet, isOpen, closeMainMenu, openMainMenu }) => {
  const blackFridayIsActive = useSelector(selectBlackFridayStatus);
  return (
    <Navigation isOpen={isOpen}>
      {(panels, controls) => (
        <>
          <button
            onClick={openMainMenu}
            className={cn(styles.menu, { [styles.blackFriday]: blackFridayIsActive })}
            {...getQaDataAttr('mobilemenu')}
          >
            <IconMenu />
            <span>{rm('navigation_menuheader')}</span>
          </button>
          <MenuDrawer
            padding="none"
            appearance={isMobile ? 'header' : 'default'}
            side="right"
            width="full"
            onClose={closeMainMenu}
            isOpen={isOpen}
            transparent
            numberOfPanels={panels.length}
            className={cn(styles.base, { [styles.blackFriday]: blackFridayIsActive })}
          >
            <nav
              style={{
                animationTimingFunction: 'ease-in',
                transition: `transform ${ANIMATION_DELAY}ms ease-in-out`,
                transform: `translateX(-${panels.length * BLOCK_WIDTH}%)`,
                minHeight: getMenuHeight({ isMobile, isTablet }) // Fixes menu height on iPhone
              }}
              className={cn(styles.nav, { [styles.blackFriday]: blackFridayIsActive })}
            >
              <Root key="root" />
              {panels}
            </nav>
            <footer className={cn(styles.footer, { [styles.blackFriday]: blackFridayIsActive })}>
              {panels.length > 0 && (
                <Button
                  appearance="secondary"
                  onClick={controls.closeLevel}
                  className={styles.close}
                >
                  {rm('navigation_backbutton')}
                </Button>
              )}
              <Button appearance="secondary" onClick={closeMainMenu} className={styles.close}>
                {rm('navigation_closemenu')}
              </Button>
            </footer>
          </MenuDrawer>
        </>
      )}
    </Navigation>
  );
});
